/**
 * AssistantChat — floating Lemy AI chat widget
 * Sits in the bottom-right corner of the app.
 * Calls POST /api/v1/assistant/chat with the full conversation history.
 * Personalizes header + suggestions based on the user's Lemy assignment.
 */

import { useEffect, useRef, useState } from "react";
import { Minus, Send, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useMyAssistant, useCurrentUser } from "@/hooks/useApi";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// ── sessionStorage persistence ────────────────────────────────────────────────
// Survives auth re-checks (ProtectedRoute remounts) and in-app navigation.
// Cleared automatically when the tab/browser is closed.
const CHAT_STORAGE_KEY = "lemy_chat_history";

function loadPersistedMessages(): Message[] {
  try {
    const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Message[]) : [];
  } catch {
    return [];
  }
}

function persistMessages(msgs: Message[]): void {
  try {
    if (msgs.length === 0) {
      sessionStorage.removeItem(CHAT_STORAGE_KEY);
    } else {
      sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(msgs));
    }
  } catch {
    // storage quota exceeded or private mode — fail silently
  }
}

// ── Posição arrastável do botão ───────────────────────────────────────────────
// null = posição padrão (canto inferior direito). Persiste entre sessões.
const POS_STORAGE_KEY = "lemy_btn_pos";
const BTN_SIZE = 56;     // w-14 h-14
const PANEL_W = 380;
const PANEL_H = 560;
const EDGE = 8;          // margem mínima da borda da tela

type Pos = { x: number; y: number };

function clampToViewport(p: Pos, w: number, h: number): Pos {
  return {
    x: Math.min(Math.max(p.x, EDGE), Math.max(EDGE, window.innerWidth - w - EDGE)),
    y: Math.min(Math.max(p.y, EDGE), Math.max(EDGE, window.innerHeight - h - EDGE)),
  };
}

function loadPersistedPos(): Pos | null {
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Pos;
    if (typeof p?.x !== "number" || typeof p?.y !== "number") return null;
    return clampToViewport(p, BTN_SIZE, BTN_SIZE);
  } catch {
    return null;
  }
}

function persistPos(p: Pos | null): void {
  try {
    if (p === null) localStorage.removeItem(POS_STORAGE_KEY);
    else localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(p));
  } catch {
    // fail silently
  }
}

interface ChatResponse {
  response: string;
  queries_executed: number;
}

// ── Role-specific suggestion chips ────────────────────────────────────────────
const SLUG_SUGGESTIONS: Record<string, string[]> = {
  curadoria:   [
    "Exposições em andamento?",
    "Obras do artista X?",
    "Próximas feiras?",
    "Obras sem foto?",
  ],
  producao:    [
    "Envios pendentes hoje?",
    "Obras a entregar?",
    "Itens com estoque baixo?",
    "Envios atrasados?",
  ],
  comercial:   [
    "Pipeline atual?",
    "Leads quentes?",
    "Obras disponíveis?",
    "Catálogos ativos?",
  ],
  ti:          [
    "Estrutura das tabelas?",
    "Últimas migrations?",
    "Usuários ativos?",
    "Tabelas do sistema?",
  ],
  financeiro:  [
    "Faturamento do mês?",
    "Obras vendidas este ano?",
    "Ticket médio?",
    "Margem por artista?",
  ],
  diretoria:   [
    "Resumo executivo do mês?",
    "Pipeline total?",
    "Envios atrasados?",
    "KPIs da galeria?",
  ],
  comunicacao: [
    "Próximos eventos públicos?",
    "Catálogos enviados?",
    "Press recente dos artistas?",
    "Artistas no site?",
  ],
};

const GENERIC_SUGGESTIONS = [
  "Quais obras estão disponíveis agora?",
  "Envios pendentes desta semana?",
  "Quantas exposições em 2024?",
  "Como cadastrar uma obra?",
];

function getSuggestions(slug: string | null | undefined): string[] {
  if (slug && SLUG_SUGGESTIONS[slug]) return SLUG_SUGGESTIONS[slug];
  return GENERIC_SUGGESTIONS;
}

// ── Widget ────────────────────────────────────────────────────────────────────

export function AssistantChat() {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState<Message[]>(loadPersistedMessages);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLTextAreaElement>(null);

  // Posição do botão (null = padrão bottom-right). Arrastar atualiza e persiste.
  const [pos, setPos] = useState<Pos | null>(loadPersistedPos);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null);
  const justDraggedRef = useRef(false);

  // Re-clampa a posição se a janela encolher (botão nunca fica fora da tela)
  useEffect(() => {
    function onResize() {
      setPos((p) => (p ? clampToViewport(p, BTN_SIZE, BTN_SIZE) : p));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function handlePointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    // Posição atual real do botão (cobre o caso pos === null, ancorado por classes)
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    // Threshold de 5px distingue clique de arrasto
    if (!d.moved && Math.hypot(dx, dy) < 5) return;
    d.moved = true;
    setPos(clampToViewport({ x: d.origX + dx, y: d.origY + dy }, BTN_SIZE, BTN_SIZE));
  }

  function handlePointerCancel() {
    // Navegador interrompeu o gesto (ex: scroll nativo) — descarta o arrasto
    dragRef.current = null;
  }

  function handlePointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const d = dragRef.current;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // captura já liberada — ignorar
    }
    if (d?.moved) {
      // Arrasto: persiste a posição; o click subsequente é suprimido via justDraggedRef
      justDraggedRef.current = true;
      setPos((p) => {
        persistPos(p);
        return p;
      });
    }
  }

  function handleClick() {
    // Click dispara após pointerup — se foi arrasto, não abre o chat.
    // Enter/Espaço no teclado também caem aqui (sem pointer events antes).
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    setOpen(true);
  }

  // Painel ancorado ao canto inferior-direito do botão, sempre dentro da tela
  const panelStyle: React.CSSProperties = pos
    ? (() => {
        const anchored = clampToViewport(
          { x: pos.x + BTN_SIZE - PANEL_W, y: pos.y + BTN_SIZE - PANEL_H },
          PANEL_W,
          PANEL_H,
        );
        return { left: anchored.x, top: anchored.y };
      })()
    : { bottom: 24, right: 24 };

  // Only fetch my-assistant when the widget is open
  const { data: myAssistant, isLoading: loadingAssistant } = useMyAssistant();
  const { data: currentUser } = useCurrentUser();

  const assigned   = myAssistant?.assigned ?? false;
  const badge      = myAssistant?.badge ?? null;       // "Lemy_Curadoria"
  const slug       = myAssistant?.slug ?? null;
  const exceeded   = myAssistant?.budget_exceeded ?? false;
  const headerName = assigned && badge ? badge : "Lemy";
  const subtitle   = assigned && badge
    ? `${badge} · Galeria Leme`
    : "Acervo · Galeria Leme";

  // First name for the welcome greeting
  const firstName =
    currentUser?.display_name?.split(" ")[0] ??
    currentUser?.email?.split("@")[0] ??
    "";

  const suggestions = getSuggestions(slug);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    const userMsg: Message = { role: "user", content };
    const next = [...messages, userMsg];

    setMessages(next);
    persistMessages(next);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const res = await api.post<ChatResponse>(
        "/assistant/chat",
        { messages: next },
        { timeoutMs: 60_000 }   // Sonnet pode levar até 50s; 15s padrão é insuficiente
      );
      const updated = [...next, { role: "assistant" as const, content: res.response }];
      setMessages(updated);
      persistMessages(updated);
    } catch (err: unknown) {
      const e = err as { data?: { detail?: string }; message?: string };
      const msg = e?.data?.detail ?? e?.message ?? "Erro ao conectar com o assistente.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function reset() {
    setMessages([]);
    persistMessages([]);
    setInput("");
    setError(null);
  }

  return (
    <>
      {/* ── Floating button (arrastável; clique abre, arrasto reposiciona) ── */}
      {!open && (
        <button
          type="button"
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          className={`fixed z-50 w-14 h-14 rounded-full bg-black text-white shadow-lg hover:scale-105 active:scale-95 flex items-center justify-center group cursor-grab active:cursor-grabbing touch-none select-none ${pos ? "" : "bottom-6 right-6 transition-transform"}`}
          style={pos ? { left: pos.x, top: pos.y } : undefined}
          title={assigned ? headerName : "Assistente IA"}
        >
          <Sparkles className="w-6 h-6 group-hover:animate-pulse" />
        </button>
      )}

      {/* ── Chat panel ── */}
      {open && (
        <div
          className="fixed z-50 flex flex-col bg-white shadow-2xl"
          style={{
            ...panelStyle,
            width: PANEL_W,
            height: PANEL_H,
            border: "1px solid rgba(0,0,0,0.12)",
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-black text-white shrink-0">
            <Sparkles className="w-4 h-4 text-[#d9ed09] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold truncate">{headerName}</div>
              <div className="text-[10px] text-white/50 truncate">{subtitle}</div>
            </div>
            <button
              type="button"
              onClick={reset}
              title="Limpar conversa"
              className="text-white/40 hover:text-white text-[10px] mr-1 transition-colors"
            >
              limpar
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              title="Minimizar"
              className="text-white/50 hover:text-white transition-colors"
            >
              <Minus className="w-4 h-4" />
            </button>
          </div>

          {/* Budget exceeded banner */}
          {exceeded && (
            <div className="px-4 py-2 bg-red-50 text-red-600 text-[11px] border-b border-red-100 shrink-0">
              Orçamento mensal esgotado. Funcionalidades limitadas até o próximo ciclo.
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">

            {/* Welcome state */}
            {messages.length === 0 && (
              <div className="space-y-4 pt-2">
                {loadingAssistant ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-black/20" />
                  </div>
                ) : (
                  <>
                    <div className="text-center">
                      <div className="w-10 h-10 rounded-full bg-[#d9ed09] flex items-center justify-center mx-auto mb-2">
                        <Sparkles className="w-5 h-5 text-black" />
                      </div>
                      <p className="text-[13px] font-semibold">
                        {firstName
                          ? `Olá, ${firstName}, vamos iniciar por quais atividades?`
                          : "Olá! Vamos iniciar por quais atividades?"}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => send(s)}
                          className="w-full text-left px-3 py-2 text-[12px] border border-black/10 hover:border-black/30 hover:bg-[#FAFAF8] transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Message bubbles */}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={[
                    "max-w-[85%] px-3 py-2 text-[13px] leading-relaxed",
                    m.role === "user"
                      ? "bg-black text-white"
                      : "bg-[#F5F5F2] text-black border-l-2 border-[#d9ed09]",
                  ].join(" ")}
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 px-3 py-2 bg-[#F5F5F2] border-l-2 border-[#d9ed09]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-black/40" />
                  <span className="text-[12px] text-black/40">Consultando…</span>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="px-3 py-2 bg-red-50 text-red-600 text-[12px] border border-red-100">
                {error}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-black/[0.08] px-3 py-2.5 flex items-end gap-2 bg-white">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Pergunte algo… (Enter para enviar)"
              rows={1}
              disabled={loading}
              className="flex-1 resize-none border border-black/15 px-3 py-2 text-[13px] focus:outline-none focus:border-black transition-colors bg-white disabled:opacity-40"
              style={{ maxHeight: 80 }}
            />
            <button
              type="button"
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="w-9 h-9 flex items-center justify-center bg-black text-white hover:bg-black/80 disabled:opacity-30 transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
